import { AxiosInstance } from "axios"
import {
    KitchenDetails,
    KitchenNews,
    ResourceItem,
    PurchaseOrder,
    ReadonlyOrder,
    ReadonlyReportDetails,
    UserLocation,
    WeekMenu,
} from "./weekMenuTypes"
import { Product } from "../../../merchant/admin/products/productsTypes"
import { ResourceFilterType } from "../../../../shared/components/createOrder/createOrderTypes"
import { ApiError } from "../../../../core/api/errorTypes"

export enum UserWeekMenu {
    ORGANIZATION_LOCATIONS_CANTEENS = "organization-locations-canteens",
    USER_WEEK_MENU = "user-week-menu",
    KITCHEN_DETAILS = "kitchen-details",
    KITCHEN_TAKEAWAY_OFFERS = "kitchen-takeaway-offers",
    KITCHEN_NEWS = "kitchen-news",
    RESOURCE_TYPE = "resource-type",
    AVAILABLE_RESOURCE = "available-resource",
    MEETING_SERVICE_LIST = "meeting-service-list",
    USER_POS_PURCHASES = "user-pos-purchases",
    USER_MEETING_SERVICE_DETAILS = "user-meeting-service-details",
    USER_MEETING_SERVICE_DETAILS_PUBLIC = "user-meeting-service-order-public",
    USER_MEETING_ORDER_FREE_BUSY_SCHEDULE = "user-meeting-order-free-busy-schedule",
}

export type GetCanteenLocationsResponse = UserLocation[]

export async function fetchUserLocations(
    {},
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetCanteenLocationsResponse> {
    try {
        const response = await axios.get("/organization/company/user/locations", {
            signal,
        })

        return response.data
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            return []
        }
        throw error
    }
}

export type GetMenuOfWeekResponse = Readonly<{
    menues?: WeekMenu[]
    externalMenuPlanning?: {
        url: string
    }
}>

export async function fetchMenuOfWeek(
    { canteenId }: { canteenId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetMenuOfWeekResponse> {
    const response = await axios.get(`/canteen/${canteenId}/menues/weekMenu/week`, {
        signal,
    })

    return response.data
}

export async function saveUserKitchenLocation(
    { locationId, kitchenId }: { locationId: number; kitchenId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetMenuOfWeekResponse> {
    const response = await axios.post(
        "/organization/company/user/location",
        {
            locationId,
            kitchenId,
        },
        {
            signal,
        },
    )

    return response.data
}

type FetchKitchenDetailsParams = {
    kitchenId: number
}

export async function fetchKitchenDetails(
    { kitchenId }: FetchKitchenDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<KitchenDetails> {
    const response = await axios.get(`/suppliers/kitchens/${kitchenId}`, {
        signal,
    })

    return response.data
}

export type KitchenTakeawayOffersResponse = {
    menues: {
        date: string
        displayDate: string
        products: Product[]
    }
    pagination: {
        nextLink: {
            rel: string
            href: string
        }
    }
}

export async function fetchKitchenTakeawayOffers(
    { kitchenId }: { kitchenId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<KitchenTakeawayOffersResponse> {
    const response = await axios.get(`/suppliers/kitchens/${kitchenId}/menues/takeaway/offers`, {
        signal,
    })

    return response.data
}

export type KitchenNewsResponse = {
    news: Array<KitchenNews>
}

export async function fetchKitchenNews(
    { locationId }: { locationId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<KitchenNewsResponse> {
    const response = await axios.get("/news", {
        params: {
            locationId,
        },
        signal,
    })

    return response.data
}

export type ResourceTypeResponse = {
    filterGroups: ResourceFilterType[]
}

export async function fetchResourceType({}, axios: AxiosInstance, signal?: AbortSignal): Promise<ResourceTypeResponse> {
    const response = await axios.get("/resourceFinder/filterData", {
        signal,
    })

    return response.data
}

export type FetchAvailableResourceParams = {
    startDateTime?: string | number | null
    meetingDuration?: string | number | null
    resourceTypeIds?: Array<number | string>
    sittings?: number | string
    meetingType?: number | string
    serviceCenterIds?: Array<number | string>
    floors?: Array<number | string>
    tableSetupIds?: Array<number | string>
    fixedAvIds?: Array<number | string>
}

export type ResourceAvailabilityResponse = Readonly<{
    resourceTypes: {
        resources: ResourceItem[]
        resourceTypeId: number
        resourceTypeName: string
        results: number
    }[]
}>

export async function fetchAvailableResource(
    params: FetchAvailableResourceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ResourceAvailabilityResponse> {
    const response = await axios.post(
        "/resourceFinder/timeSlots",
        {
            ...params,
        },
        {
            signal,
        },
    )

    return response.data
}

type FetchUserMeetingServiceParams = {
    userId: number
    startDate?: string
    endDate?: string
    view?: string
}

export type FetchUserMeetingServiceResponse = {
    reportDetails: ReadonlyReportDetails
    orders: Array<ReadonlyOrder>
}

export async function fetchUserMeetingService(
    { userId, endDate, startDate, view }: FetchUserMeetingServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchUserMeetingServiceResponse> {
    const response = await axios.get(`/users/${userId}/upcoming/meetingService`, {
        params: {
            startDate,
            endDate,
            view,
        },
        signal,
    })

    return response.data
}

export type FetchUserPosResponse = {
    orders: ReadonlyArray<PurchaseOrder>
}

export async function fetchUserPos(
    { userId, endDate, startDate, view }: FetchUserMeetingServiceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchUserPosResponse> {
    const response = await axios.get(`/users/${userId}/upcoming/pos`, {
        params: {
            startDate,
            endDate,
            view,
        },
        signal,
    })

    return response.data
}

export type FetchOneTimeTokenResponse = {
    token: string
    tokenType: string
}

export async function fetchOneTimeToken(
    {}: {},
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchOneTimeTokenResponse> {
    const response = await axios.post(
        "/security/tokens",
        {
            tokenType: "ONE_TIME_TOKEN",
        },
        {
            signal,
        },
    )

    return response.data
}

